import React, { useEffect } from 'react';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import './styles.scss';

// Every "Request a demo" on this page books through the same scheduler.
const DEMO_SCHEDULER_URL = 'https://scheduler.zoom.us/d/561f8hkb/lusidpay-demo';

const openDemoScheduler = () =>
  window.open(DEMO_SCHEDULER_URL, '_blank', 'noopener,noreferrer');

// ─────────────────────────────────────────────────────────────────────────────
// Review markers
//
// The source copy carries unresolved items that must not ship as published
// claims. They render flagged rather than silently dropped, so the section
// structure survives review. Delete <ReviewNote> and its call sites once the
// real copy lands.
// ─────────────────────────────────────────────────────────────────────────────

const ReviewNote = ({ children }) => (
  <Box component="span" className="cert-review-note">
    {children}
    <span className="cert-review-note__flag"></span>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const burdenFigures = [
  {
    figure: '~2 hours',
    caption: "DOT's estimated average burden to complete a review and reach a disposition, per certification.",
    accent: false,
  },
  {
    figure: 'Under 20 minutes',
    caption: 'Target adjudication time per file with LUSID Certify, reviewer decision included.',
    accent: true,
    note: 'State the basis — internal benchmark, pilot measurement, or design target. A procurement reviewer will ask, and the answer has to be defensible.',
  },
  {
    figure: 'Every file',
    caption: 'Each disposition carries an automatic citation to the governing section of Part 26 or Part 23, producing a uniform record across reviewers.',
    accent: false,
  },
];

const components = [
  {
    id: 'assistance',
    tag: 'Component 01',
    statusMod: 'live',
    title: 'Technical Assistance Co-Pilot',
    who: 'Public-facing. Embedded on your UCP or airport certification website.',
    body: [
      'A guided interview that answers applicant questions about the personal narrative, the personal net worth statement, eligibility criteria, and the requirements of Part 26 — at the point where the applicant is stuck, rather than by telephone three days later. Because every applicant now has to produce an individualized narrative, the volume and difficulty of these questions has risen sharply.',
    ],
    bullets: [
      'Answers grounded in the current text of Parts 26 and 23',
      'Deflects inbound technical assistance calls and email',
      'Deploys on your existing website with no internal integration',
      'Scales by annual application volume, from single agencies to statewide programs',
    ],
    cta: true,
  },
  {
    id: 'adjudication',
    tag: 'Component 02',
    statusMod: 'live',
    title: 'Adjudication Dashboard & Co-Pilot',
    who: 'Agency-side. For certifying officers and analysts.',
    body: [
      'An automated analyst. It reads the personal narrative and supporting documentation, applies your UCP’s review rubric, and returns a recommendation with the governing citation attached. The certifying officer decides; the platform assembles the record behind the decision.',
    ],
    bullets: [
      'Personal narrative analysis against the preponderance-of-the-evidence standard, evaluated holistically as the rule requires',
      'Personal net worth screening against the current cap, with automatic flagging',
      'Business size standard validation',
      'Ownership and control review',
      'Multi-stage reviewer workflows and configurable scoring weights',
      'Automatic citation to the governing section on every disposition',
    ],
    footer: 'The rubric is yours. LUSID Certify encodes the review framework your team already uses rather than imposing a different one — the same standard, applied faster and recorded uniformly.',
    cta: true,
  },
  {
    id: 'readiness',
    tag: 'Component 03',
    statusMod: 'soon',
    title: 'Application Readiness Dashboard & Co-Pilot',
    body: [
      'A guided interview that helps a firm draft a compliant personal narrative before submission, reducing requests for further information and resubmission cycles. Available directly to applicants, or in sponsored blocks that agencies and prime contractors distribute to at-risk firms as part of federally mandated supportive services programs.',
    ],
    cta: true,
  },
];

const deploymentFacts = [
  {
    term: 'Integration required',
    detail: 'None. The technical assistance component deploys on your public website. The adjudication component runs alongside your existing platform.',
  },
  {
    term: 'Optional data exchange',
    detail: 'SFTP available where an agency wants structured exchange with an existing system.',
  },
  {
    term: 'System of record',
    detail: 'Remains yours. LUSID Certify produces analysis and recommendations; dispositions are entered and retained in your platform.',
  },
  {
    term: 'Decision authority',
    detail: 'Remains with the certifying officer. The platform recommends and documents; it does not certify or decertify.',
  },
];

const scaleFigures = [
  { n: '53', label: 'State departments of transportation administering DBE programs' },
  { n: '500+', label: 'Transit agencies' },
  { n: '~400', label: 'Airport sponsors operating ACDBE programs' },
];

const FactList = ({ facts }) => (
  <Box component="dl" className="cert-facts">
    {facts.map(({ term, detail, pending }) => (
      <Box key={term} className="cert-facts__row">
        <Typography component="dt" className="cert-facts__term">{term}</Typography>
        <Typography component="dd" className="cert-facts__detail">
          {pending ? <ReviewNote>{pending}</ReviewNote> : detail}
        </Typography>
      </Box>
    ))}
  </Box>
);

const CertifyPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box className="cert-page">

      {/* ── Regulatory basis strip ───────────────────────────── */}
      <Box className="cert-status">
        <Container maxWidth="lg">
          <Typography className="cert-status__text">
            <strong>Regulatory basis:</strong> 49 CFR Part 26 and Part 23 as amended by the interim
            final rule effective October 3, 2025 (Docket DOT–OST–2025–0897).
          </Typography>
        </Container>
      </Box>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box className="cert-hero">
        <Box className="cert-hero__grid-bg" />
        <Container maxWidth="lg">
          <Box className="cert-hero__content">
            <Typography className="cert-hero__overline">LUSID Certify</Typography>
            <Typography variant="h1" className="cert-hero__title">
              Every certification decision now rests on a personal narrative.
            </Typography>
            <Typography className="cert-hero__subtitle">
              Under the amended Parts 26 and 23, each applicant must establish social and economic
              disadvantage individually, by a preponderance of the evidence, and each narrative must
              be evaluated holistically on the record. LUSID Certify supports that review — reading
              narratives and supporting documentation, applying your UCP's own rubric, and returning
              a recommendation with the governing citation attached. It deploys without touching
              agency IT.
            </Typography>
            <Box className="cert-hero__cta-row">
              <Button
                variant="contained"
                className="cert-hero__cta"
                onClick={openDemoScheduler}
              >
                Request a demo
              </Button>
              <Typography className="cert-hero__note">
                Built for UCP directors, transit certification managers, and airport ACDBE compliance
                officers.
              </Typography>
            </Box>

            {/* Regulatory change callout */}
            <Box className="cert-change">
              <Typography className="cert-change__title">
                What changed, and why the workload changed with it
              </Typography>
              <Typography className="cert-change__body">
                Certification review used to turn substantially on group membership. It no longer
                does. The presumptions were removed, the burden moved to the applicant firm, and what
                arrives on a certifying officer's desk is now an unstructured narrative that has to
                be weighed on its individual merits and defended in writing.
              </Typography>
              <Typography className="cert-change__body">
                That is a harder task than the one it replaced, and it arrives at every UCP at once —
                alongside the reevaluation of the existing certified population required under
                § 26.111 and § 23.81, during which recipients may not set DBE contract goals or count
                participation toward them.
              </Typography>
              <Typography component="cite" className="cert-change__cite">
                49 CFR §§ 26.67, 26.111, 26.51(h), 26.55(i); 23.81. Interim final rule, 90 FR,
                effective October 3, 2025.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── The arithmetic ───────────────────────────────────── */}
      <Box className="cert-section">
        <Container maxWidth="lg">
          <Box className="cert-section__head">
            <Typography className="cert-section__overline">The arithmetic</Typography>
            <Typography variant="h2" className="cert-section__title">
              The burden is per file, and the file count did not go down
            </Typography>
            <Typography className="cert-section__lede">
              DOT's own regulatory analysis for the interim final rule estimates the review burden at
              roughly two hours per certification disposition. That figure applies to each
              reevaluation and to each new application, on top of an agency's existing caseload, with
              no additional staff attached to it.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {burdenFigures.map(({ figure, caption, accent, note }) => (
              <Grid item xs={12} md={4} key={figure}>
                <Box className="cert-burden">
                  <Typography className={`cert-burden__figure${accent ? ' cert-burden__figure--accent' : ''}`}>
                    {figure}
                  </Typography>
                  <Typography className="cert-burden__caption">
                    {caption}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── The platform ─────────────────────────────────────── */}
      <Box className="cert-section cert-section--tinted" id="cert-products">
        <Container maxWidth="lg">
          <Box className="cert-section__head">
            <Typography className="cert-section__overline">The platform</Typography>
            <Typography variant="h2" className="cert-section__title">
              Three components, deployed in the order that fits your program
            </Typography>
            <Typography className="cert-section__lede">
              Most agencies begin with the public-facing assistant, because it reduces inbound volume
              immediately and touches no internal system. The adjudication platform follows.
            </Typography>
          </Box>

          <Box className="cert-components">
            {components.map((c) => (
              <Box className="cert-component" id={c.id} key={c.id}>
                <Box className="cert-component__aside">
                  <Typography className="cert-component__tag">{c.tag}</Typography>
                </Box>
                <Box className="cert-component__main">
                  <Typography variant="h3" className="cert-component__title">{c.title}</Typography>
                  <Typography className="cert-component__who">{c.who}</Typography>
                  {c.body.map((p) => (
                    <Typography className="cert-component__body" key={p}>{p}</Typography>
                  ))}
                  {c.bullets && (
                    <Box component="ul" className="cert-component__list">
                      {c.bullets.map((b) => (
                        <Box component="li" key={b}>{b}</Box>
                      ))}
                    </Box>
                  )}
                  {c.footer && (
                    <Typography className="cert-component__body">{c.footer}</Typography>
                  )}
                  {c.footnote && (
                    <Typography className="cert-component__footnote">{c.footnote}</Typography>
                  )}
                  {c.cta && (
                    <Button
                      variant="text"
                      className="cert-component__cta"
                      onClick={openDemoScheduler}
                    >
                      Request a demo →
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Deployment ───────────────────────────────────────── */}
      <Box className="cert-section">
        <Container maxWidth="lg">
          <Box className="cert-section__head">
            <Typography className="cert-section__overline">Deployment</Typography>
            <Typography variant="h2" className="cert-section__title">
              It does not replace the system your program runs on
            </Typography>
            <Typography className="cert-section__lede">
              LUSID Certify is an analysis layer, not a system of record. It is not a replacement for
              B2G Now or PRISM and does not ask you to migrate off either one. That is deliberate:
              replacing a certification system of record is a multi-year procurement, and the backlog
              is a present problem.
            </Typography>
          </Box>
          <FactList facts={deploymentFacts} />
        </Container>
      </Box>

      {/* ── Scale ────────────────────────────────────────────── */}
      <Box className="cert-section cert-section--tinted">
        <Container maxWidth="lg">
          <Box className="cert-section__head">
            <Typography className="cert-section__overline">
              Who administers these programs
            </Typography>
            <Typography variant="h2" className="cert-section__title">
              The reevaluation reached every certifying body at once
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {scaleFigures.map(({ n, label }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Box className="cert-scale">
                  <Typography className="cert-scale__figure">{n}</Typography>
                  <Typography className="cert-scale__label">{label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography className="cert-section__footnote">
            Some UCPs have completed reevaluation; others are still working through it. Either way,
            the individualized narrative standard now applies to every new application permanently —
            the reevaluation was the spike, not the change.
          </Typography>
        </Container>
      </Box>

      {/* ── Disclosure ───────────────────────────────────────── */}
      <Box className="cert-disclosure">
        <Container maxWidth="lg">
          <Typography className="cert-disclosure__text">
            LUSID Certify produces analysis and recommendations to support certification review. It
            does not make certification decisions, and it is not legal advice. Certification and
            decertification authority remains with the recipient and its Unified Certification
            Program under 49 CFR Parts 26 and 23. Regulatory references on this page describe Parts
            26 and 23 as amended by the interim final rule effective October 3, 2025; agencies should
            confirm current requirements against the eCFR and DOT guidance.{' '}
          </Typography>
        </Container>
      </Box>

    </Box>
  );
};

export default CertifyPage;
